import { type SDKRequestFor } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { SdkClient } from '~/effects'
import { runSdkRequest } from '~/features/module-runtime'
import { reportHandledError } from '../error-reporting/report-handled-error'

type ActiveState = {
  organizationId: string
  folderIds: string[]
  quotaExceeded: number | null
} & (
  | { status: 'editTemplate' }
  | { status: 'previewRecipients' }
  | { status: 'sending'; sendableFolderIds: string[] }
  | { status: 'sent' }
)

export type BulkSendClientOrganizersState = { status: 'idle' } | ActiveState

export type BulkSendClientOrganizersMessage =
  | { type: 'editTemplate' }
  | { type: 'previewRecipients' }
  | { type: 'send'; sendableFolderIds: string[] }
  | { type: 'sendCompleted' }
  | { type: 'quotaExceeded'; quota: number }
  | { type: 'requestFailed' }

export function idleBulkSendClientOrganizers(): BulkSendClientOrganizersState {
  return { status: 'idle' }
}

export function openBulkSendClientOrganizers(args: {
  organizationId: string
  folderIds: string[]
}): BulkSendClientOrganizersState {
  return {
    status: 'editTemplate',
    organizationId: args.organizationId,
    folderIds: args.folderIds,
    quotaExceeded: null
  }
}

/**
 * The bulk-send chart owns one send attempt from template editing through completion. Route
 * Entry, folder normalization, Command launch, and closing belong to the folder-list parent;
 * this chart owns the domain transitions and contains no parent-lifetime bookkeeping.
 */
export function updateBulkSendClientOrganizers(
  state: BulkSendClientOrganizersState,
  message: BulkSendClientOrganizersMessage
): BulkSendClientOrganizersState {
  return match({ state, message })
    .returnType<BulkSendClientOrganizersState>()
    .with(
      {
        state: { status: 'sending' },
        message: { type: P.union('editTemplate', 'previewRecipients', 'send') }
      },
      ({ state }) => state
    )
    .with({ state: { status: P.not('idle') }, message: { type: 'editTemplate' } }, ({ state }) => ({
      ...state,
      status: 'editTemplate'
    }))
    .with(
      { state: { status: P.not('idle') }, message: { type: 'previewRecipients' } },
      ({ state }) => ({ ...state, status: 'previewRecipients' })
    )
    .with(
      { state: { status: 'previewRecipients' }, message: { type: 'send' } },
      ({ state, message }) => ({
        ...state,
        status: 'sending',
        sendableFolderIds: message.sendableFolderIds,
        quotaExceeded: null
      })
    )
    .with({ message: { type: 'send' } }, ({ state }) => state)
    .with(
      {
        state: { status: 'sending' },
        message: { type: 'sendCompleted' }
      },
      ({ state }) => ({ ...sendingContext(state), status: 'sent' })
    )
    .with(
      {
        state: { status: 'sending' },
        message: { type: 'quotaExceeded' }
      },
      ({ state, message }) => ({
        ...sendingContext(state),
        status: 'previewRecipients',
        quotaExceeded: message.quota
      })
    )
    .with(
      {
        state: { status: 'sending' },
        message: { type: 'requestFailed' }
      },
      ({ state }) => ({ ...sendingContext(state), status: 'previewRecipients' })
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function sendingContext(state: Extract<ActiveState, { status: 'sending' }>) {
  const { sendableFolderIds: _sendableFolderIds, ...context } = state
  return context
}

function unexpectedMessage(
  state: BulkSendClientOrganizersState,
  message: BulkSendClientOrganizersMessage
): never {
  throw new Error(`Unexpected bulk-send message ${message.type} while ${state.status}`)
}

export type BulkSendClientOrganizersInput = {
  organizationId: string
  folderIds: string[]
}

export function sendClientOrganizersEffect(args: BulkSendClientOrganizersInput) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const request = {
      type: 'folders/bulkSendClientOrganizerEmails',
      organizationId: args.organizationId,
      folderIds: args.folderIds
    } satisfies SDKRequestFor<'folders/bulkSendClientOrganizerEmails'>

    const result = yield* runSdkRequest(sdk.send(request), {
      onError: (error) =>
        reportHandledError(error, {
          source: 'st-folder-list',
          operation: request.type,
          extra: { request }
        })
    })

    if (!result.ok) {
      return { type: 'requestFailed' } as const
    }

    if (result.response.type == 'quota_exceeded') {
      if (typeof result.response.quota != 'number') {
        return yield* Effect.die(
          new Error('bulkSendClientOrganizerEmails: expected quota when quota_exceeded')
        )
      }
      return {
        type: 'quotaExceeded',
        quota: result.response.quota
      } as const
    }

    return { type: 'sendCompleted' } as const
  })
}
